export function generateTattooPreview(tp, params) {
    
    let sourceChannel = null;
    let nonAlphaRegion = null;
    
    for (const channel of tp.uvChannels) {
        if (!channel?.decalCanvas) continue;
        
        const canvas = channel.decalCanvas;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Find bounding box of non-alpha pixels
        let minX = canvas.width, minY = canvas.height;
        let maxX = 0, maxY = 0;
        let hasContent = false;
        
        for (let y = 0; y < canvas.height; y++) {
            for (let x = 0; x < canvas.width; x++) {
                const idx = (y * canvas.width + x) * 4;
                if (data[idx + 3] > 0) {
                    hasContent = true;
                    minX = Math.min(minX, x);
                    minY = Math.min(minY, y);
                    maxX = Math.max(maxX, x);
                    maxY = Math.max(maxY, y);
                }
            }
        }
        
        if (hasContent) {
            sourceChannel = channel;
            nonAlphaRegion = { minX, minY, maxX, maxY };
            break;
        }
    }
    
    if (!sourceChannel || !nonAlphaRegion) {
        return null;
    }
    
    const regionWidth = nonAlphaRegion.maxX - nonAlphaRegion.minX + 1;
    const regionHeight = nonAlphaRegion.maxY - nonAlphaRegion.minY + 1;
    const regionSize = Math.max(regionWidth, regionHeight);
    
    const centerX = (nonAlphaRegion.minX + nonAlphaRegion.maxX) / 2;
    const centerY = (nonAlphaRegion.minY + nonAlphaRegion.maxY) / 2;
    const halfSize = regionSize / 2;
    
    const cropX = Math.max(0, Math.floor(centerX - halfSize));
    const cropY = Math.max(0, Math.floor(centerY - halfSize));
    const cropSize = Math.min(regionSize, 
        Math.min(sourceChannel.decalCanvas.width - cropX, sourceChannel.decalCanvas.height - cropY));
    
    const previewCanvas = document.createElement('canvas');
    previewCanvas.width = 256;
    previewCanvas.height = 256;
    const previewCtx = previewCanvas.getContext('2d');
    
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = cropSize;
    tempCanvas.height = cropSize;
    const tempCtx = tempCanvas.getContext('2d');
    
    tempCtx.drawImage(sourceChannel.decalCanvas, 
        cropX, cropY, cropSize, cropSize,
        0, 0, cropSize, cropSize);
    
    previewCtx.imageSmoothingEnabled = true;
    previewCtx.imageSmoothingQuality = 'high';
    previewCtx.drawImage(tempCanvas, 0, 0, 256, 256);
    
    applyTattooLookToCanvas(previewCanvas, params);
    
    return previewCanvas;
}

function applyTattooLookToCanvas(canvas, params) {
    const { opacity, blendMix, edgeBlur, noise, bleed } = params;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    
    const makeScratch = (w, h) => {
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        return { c, ctx: c.getContext('2d') };
    };
    
    const { ctx: mask } = makeScratch(w, h);
    const { ctx: outsideCtx } = makeScratch(w, h);
    mask.clearRect(0, 0, w, h);
    outsideCtx.clearRect(0, 0, w, h);
    mask.drawImage(canvas, 0, 0);
    let maskData = mask.getImageData(0, 0, w, h);
    
    for (let i = 0; i < maskData.data.length; i += 4) {
        const a = maskData.data[i + 3];
        maskData.data[i] = a;
        maskData.data[i + 1] = a;
        maskData.data[i + 2] = a;
        maskData.data[i + 3] = 255;
    }
    mask.putImageData(maskData, 0, 0);

    const invData = new ImageData(new Uint8ClampedArray(maskData.data), w, h);
    for (let i = 0; i < invData.data.length; i += 4) {
        const a = invData.data[i];
        const inv = 255 - a;
        invData.data[i] = inv;
        invData.data[i + 1] = inv;
        invData.data[i + 2] = inv;
        invData.data[i + 3] = 255;
    }
    outsideCtx.putImageData(invData, 0, 0);
    
    const clampedBlur = Math.min(0.5, Math.max(0, edgeBlur || 0));
    const radius = 1;
    const blurPass = (ctxObj) => {
        let img = ctxObj.getImageData(0, 0, w, h);
        const src = img.data;
        const dst = new Uint8ClampedArray(src.length);
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                let sum = 0, count = 0;
                for (let k = -radius; k <= radius; k++) {
                    const xx = Math.min(w - 1, Math.max(0, x + k));
                    const idx = (y * w + xx) * 4;
                    sum += src[idx];
                    count++;
                }
                const out = (y * w + x) * 4;
                const v = (sum / count) | 0;
                dst[out] = dst[out + 1] = dst[out + 2] = v;
                dst[out + 3] = 255;
            }
        }
        img.data.set(dst);
        ctxObj.putImageData(img, 0, 0);
        img = ctxObj.getImageData(0, 0, w, h);
        const src2 = img.data;
        const dst2 = new Uint8ClampedArray(src2.length);
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                let sum = 0, count = 0;
                for (let k = -radius; k <= radius; k++) {
                    const yy = Math.min(h - 1, Math.max(0, y + k));
                    const idx = (yy * w + x) * 4;
                    sum += src2[idx];
                    count++;
                }
                const out = (y * w + x) * 4;
                const v = (sum / count) | 0;
                dst2[out] = dst2[out + 1] = dst2[out + 2] = v;
                dst2[out + 3] = 255;
            }
        }
        img.data.set(dst2);
        ctxObj.putImageData(img, 0, 0);
    };
    if (clampedBlur > 0) {
        blurPass(mask);
        blurPass(outsideCtx);
    }
    
    const { ctx: noiseCtx } = makeScratch(w, h);
    
    const noiseImg = noiseCtx.createImageData(w, h);
    const ndata = noiseImg.data;
    
    let seed = 12345;
    const random = () => {
        seed = (seed * 16807) % 2147483647;
        return seed / 2147483647;
    };
    
    const rawNoise = new Array(w * h);
    for (let i = 0; i < rawNoise.length; i++) {
        rawNoise[i] = random();
    }
    
    const smoothedNoise = [...rawNoise];
    const blurRadius = 1.5;
    
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            let sum = 0;
            let count = 0;
            const range = Math.ceil(blurRadius);
            
            for (let dx = -range; dx <= range; dx++) {
                const nx = x + dx;
                if (nx >= 0 && nx < w) {
                    const weight = Math.exp(-(dx * dx) / (2 * blurRadius * blurRadius));
                    sum += rawNoise[y * w + nx] * weight;
                    count += weight;
                }
            }
            smoothedNoise[y * w + x] = sum / count;
        }
    }
    
    const finalNoise = [...smoothedNoise];
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            let sum = 0;
            let count = 0;
            const range = Math.ceil(blurRadius);
            
            for (let dy = -range; dy <= range; dy++) {
                const ny = y + dy;
                if (ny >= 0 && ny < h) {
                    const weight = Math.exp(-(dy * dy) / (2 * blurRadius * blurRadius));
                    sum += smoothedNoise[ny * w + x] * weight;
                    count += weight;
                }
            }
            finalNoise[y * w + x] = sum / count;
        }
    }
    
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const idx = (y * w + x) * 4;
            let noise = finalNoise[y * w + x];
            
            noise = 0.5 + (noise - 0.5) * 0.8;
            
            const noiseValue = Math.round(Math.max(0, Math.min(1, noise)) * 255);
            
            ndata[idx] = noiseValue;
            ndata[idx + 1] = noiseValue;
            ndata[idx + 2] = noiseValue;
            ndata[idx + 3] = 255;
        }
    }
    noiseCtx.putImageData(noiseImg, 0, 0);
    
    const maskBlurred = mask.getImageData(0, 0, w, h);
    const noiseData = noiseCtx.getImageData(0, 0, w, h);
    const noiseArr = noiseData.data;
    const maskArr = maskBlurred.data;
    for (let i = 0; i < noiseArr.length; i += 4) {
        const m = maskArr[i] / 255;
        const strength = Math.min(1, noise * 2.0) * m;
        noiseArr[i + 3] = Math.min(255, Math.max(0, Math.round(strength * 255)));
    }
    noiseCtx.putImageData(noiseData, 0, 0);

    const dImg = ctx.getImageData(0, 0, w, h);
    const d = dImg.data;
    const nz = noiseCtx.getImageData(0, 0, w, h).data;
    const neutral = 128;
    
    for (let i = 0; i < d.length; i += 4) {
        const origA = d[i + 3] / 255;
        if (origA <= 0) continue;
        
        let softA = maskBlurred.data[i] / 255;
        softA = Math.max(softA, Math.min(0.9, origA * 0.5));
        
        const r = d[i], g = d[i+1], b = d[i+2];
        const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        
        const nAlpha = nz[i + 3] / 255;
        const nGray = nz[i] / 255;
        const lumaNoise = (nGray - 0.5) * (0.35 * nAlpha) * softA;
        const newLuma = Math.max(0, Math.min(255, luma * (1.0 + lumaNoise)));
        
        const oldLuma = luma > 1e-5 ? luma : 1.0;
        const scale = newLuma / oldLuma;
        let nr = Math.max(0, Math.min(255, r * scale));
        let ng = Math.max(0, Math.min(255, g * scale));
        let nb = Math.max(0, Math.min(255, b * scale));
        
        const bleedFactor = bleed * softA;
        if (bleedFactor > 0) {
            const bleedR = 180, bleedG = 20, bleedB = 20;
            nr = Math.round(nr * (1 - bleedFactor) + bleedR * bleedFactor);
            ng = Math.round(ng * (1 - bleedFactor) + bleedG * bleedFactor);
            nb = Math.round(nb * (1 - bleedFactor) + bleedB * bleedFactor);
        }
        
        const wMix = blendMix * softA;
        nr = Math.round((nr * (1 - wMix)) + ((nr * neutral) / 255) * wMix);
        ng = Math.round((ng * (1 - wMix)) + ((ng * neutral) / 255) * wMix);
        nb = Math.round((nb * (1 - wMix)) + ((nb * neutral) / 255) * wMix);
        
        d[i] = nr;
        d[i+1] = ng;
        d[i+2] = nb;
        
        const alphaNoise = (nGray - 0.5) * (0.15 * nAlpha) * softA;
        const adjustedAlpha = origA * (1.0 + alphaNoise);
        const newA = Math.round(255 * Math.max(0, Math.min(1, adjustedAlpha * opacity)));
        d[i + 3] = newA;
    }
    
    ctx.putImageData(dImg, 0, 0);
}

export function applyTattooLook2D(tp, params) {
    const { opacity, blendMix, edgeBlur, noise, bleed } = params;

    const makeScratch = (w, h) => {
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        return { c, ctx: c.getContext('2d') };
    };

    tp.uvChannels.forEach((channel, index) => {
        if (!channel || !channel.baseCanvas || !channel.decalCanvas) return;

        const decals = channel.decalCanvas;
        const w = decals.width, h = decals.height;

        const { c: maskC, ctx: mask } = makeScratch(w, h);
        mask.clearRect(0, 0, w, h);
        mask.drawImage(decals, 0, 0);
        let maskData = mask.getImageData(0, 0, w, h);

        for (let i = 0; i < maskData.data.length; i += 4) {
            const a = maskData.data[i + 3];
            maskData.data[i] = a;
            maskData.data[i + 1] = a;
            maskData.data[i + 2] = a;
            maskData.data[i + 3] = 255;
        }
        mask.putImageData(maskData, 0, 0);

        const passes = Math.max(0, Math.min(10, Math.round((edgeBlur || 0))));
        if (passes > 0) {
            const radius = 1;
            const blurPass = () => {
                let img = mask.getImageData(0, 0, w, h);
                const src = img.data;
                const dst = new Uint8ClampedArray(src.length);
                for (let y = 0; y < h; y++) {
                    for (let x = 0; x < w; x++) {
                        let sum = 0, count = 0;
                        for (let k = -radius; k <= radius; k++) {
                            const xx = Math.min(w - 1, Math.max(0, x + k));
                            const idx = (y * w + xx) * 4;
                            sum += src[idx]; // red holds the mask value
                            count++;
                        }
                        const out = (y * w + x) * 4;
                        const v = (sum / count) | 0;
                        dst[out] = dst[out + 1] = dst[out + 2] = v;
                        dst[out + 3] = 255;
                    }
                }
                img.data.set(dst);
                mask.putImageData(img, 0, 0);

                img = mask.getImageData(0, 0, w, h);
                const src2 = img.data;
                const dst2 = new Uint8ClampedArray(src2.length);
                for (let y = 0; y < h; y++) {
                    for (let x = 0; x < w; x++) {
                        let sum = 0, count = 0;
                        for (let k = -radius; k <= radius; k++) {
                            const yy = Math.min(h - 1, Math.max(0, y + k));
                            const idx = (yy * w + x) * 4;
                            sum += src2[idx];
                            count++;
                        }
                        const out = (y * w + x) * 4;
                        const v = (sum / count) | 0;
                        dst2[out] = dst2[out + 1] = dst2[out + 2] = v;
                        dst2[out + 3] = 255;
                    }
                }
                img.data.set(dst2);
                mask.putImageData(img, 0, 0);
            };
            for (let p = 0; p < passes; p++) blurPass();
        }

        const { ctx: bleedCtx } = makeScratch(w, h);
        bleedCtx.clearRect(0, 0, w, h);
        bleedCtx.globalAlpha = bleed;
        bleedCtx.globalCompositeOperation = 'source-over';
        bleedCtx.drawImage(maskC, 0, 0);
        bleedCtx.globalCompositeOperation = 'source-in';
        bleedCtx.fillStyle = 'rgba(180,20,20,1)';
        bleedCtx.fillRect(0, 0, w, h);

        const { ctx: noiseCtx } = makeScratch(w, h);
        
        const noiseImg = noiseCtx.createImageData(w, h);
        const ndata = noiseImg.data;
        
        let seed = 12345;
        const random = () => {
            seed = (seed * 16807) % 2147483647;
            return seed / 2147483647;
        };
        
        const rawNoise = new Array(w * h);
        for (let i = 0; i < rawNoise.length; i++) {
            rawNoise[i] = random();
        }
        
        const smoothedNoise = [...rawNoise];
        const blurRadius = 1.5;
        
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                let sum = 0;
                let count = 0;
                const range = Math.ceil(blurRadius);
                
                for (let dx = -range; dx <= range; dx++) {
                    const nx = x + dx;
                    if (nx >= 0 && nx < w) {
                        const weight = Math.exp(-(dx * dx) / (2 * blurRadius * blurRadius));
                        sum += rawNoise[y * w + nx] * weight;
                        count += weight;
                    }
                }
                smoothedNoise[y * w + x] = sum / count;
            }
        }
        
        const finalNoise = [...smoothedNoise];
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                let sum = 0;
                let count = 0;
                const range = Math.ceil(blurRadius);
                
                for (let dy = -range; dy <= range; dy++) {
                    const ny = y + dy;
                    if (ny >= 0 && ny < h) {
                        const weight = Math.exp(-(dy * dy) / (2 * blurRadius * blurRadius));
                        sum += smoothedNoise[ny * w + x] * weight;
                        count += weight;
                    }
                }
                finalNoise[y * w + x] = sum / count;
            }
        }
        
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                const idx = (y * w + x) * 4;
                let noise = finalNoise[y * w + x];
                
                noise = 0.5 + (noise - 0.5) * 0.8;
                
                const noiseValue = Math.round(Math.max(0, Math.min(1, noise)) * 255);
                
                ndata[idx] = noiseValue;
                ndata[idx + 1] = noiseValue;
                ndata[idx + 2] = noiseValue;
                ndata[idx + 3] = 255;
            }
        }
        noiseCtx.putImageData(noiseImg, 0, 0);

        const maskBlurred = mask.getImageData(0, 0, w, h);
        const noiseData = noiseCtx.getImageData(0, 0, w, h);
        const noiseArr = noiseData.data;
        const maskArr = maskBlurred.data;
        for (let i = 0; i < noiseArr.length; i += 4) {
            const m = maskArr[i] / 255;
            const strength = Math.min(1, noise * 2.0) * m;
            noiseArr[i + 3] = Math.min(255, Math.max(0, Math.round(strength * 255)));
        }
        noiseCtx.putImageData(noiseData, 0, 0);

        const blurredMaskData = mask.getImageData(0, 0, w, h);

        const dCtx = decals.getContext('2d');
        const dImg = dCtx.getImageData(0, 0, w, h);
        const d = dImg.data;
        const nzImg = noiseCtx.getImageData(0, 0, w, h);
        const nz = nzImg.data;

        const neutral = 128;

        for (let i = 0; i < d.length; i += 4) {
            const origA = d[i + 3] / 255;
            if (origA <= 0) continue;

            // Minimal soften as weight only; do not let weight be zero where decal exists
            let softA = blurredMaskData.data[i] / 255;
            softA = Math.max(softA, Math.min(0.9, origA * 0.5));

            // Compute current luma for luma-only noise modulation
            const r = d[i], g = d[i+1], b = d[i+2];
            const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;

            // Micro-noise modulation on luma only, gated by weight
            const nAlpha = nz[i + 3] / 255;
            const nGray = nz[i] / 255; // noise gray 0..1
            // Isotropic luma noise; amplitude tuned to avoid visible striping
            const lumaNoise = (nGray - 0.5) * (0.35 * nAlpha) * softA;
            const newLuma = Math.max(0, Math.min(255, luma * (1.0 + lumaNoise)));

            // Reconstruct color with preserved chroma by scaling rgb toward new luma
            const oldLuma = luma > 1e-5 ? luma : 1.0;
            const scale = newLuma / oldLuma;
            let nr = Math.max(0, Math.min(255, r * scale));
            let ng = Math.max(0, Math.min(255, g * scale));
            let nb = Math.max(0, Math.min(255, b * scale));

            // Subtle warm bleed (tiny; modulated by weight)
            const bleedFactor = bleed * softA;
            if (bleedFactor > 0) {
                const bleedR = 180, bleedG = 20, bleedB = 20;
                nr = Math.round(nr * (1 - bleedFactor) + bleedR * bleedFactor);
                ng = Math.round(ng * (1 - bleedFactor) + bleedG * bleedFactor);
                nb = Math.round(nb * (1 - bleedFactor) + bleedB * bleedFactor);
            }

            // Multiply-darken toward neutral proportional to blendMix and weight
            const wMix = blendMix * softA;
            nr = Math.round((nr * (1 - wMix)) + ((nr * neutral) / 255) * wMix);
            ng = Math.round((ng * (1 - wMix)) + ((ng * neutral) / 255) * wMix);
            nb = Math.round((nb * (1 - wMix)) + ((nb * neutral) / 255) * wMix);

            d[i]   = nr;
            d[i+1] = ng;
            d[i+2] = nb;

            // Apply noise to alpha channel for pore-like transparency effect
            const alphaNoise = (nGray - 0.5) * (0.15 * nAlpha) * softA;
            const adjustedAlpha = origA * (1.0 + alphaNoise);
            const newA = Math.round(255 * Math.max(0, Math.min(1, adjustedAlpha * opacity)));
            d[i + 3] = newA;
        }

        dCtx.putImageData(dImg, 0, 0);

        // After modifying decals in-place, composite channel to update final material
        tp.compositeTexture(index);
    });

    // Refresh once after all channels processed
    tp.refreshCharacterMaterials?.();
    tp.updateStatus?.('Tattoo look applied (decal layer updated)');
}
